/**
 * Add-on products (CALLOUT) - merge add-ons into SAME cart request + reliable deselection sync
 * + Tooltip (question + answer) via AddOnTip
 *
 * FIX (Search jump / weird behavior):
 * - Do NOT run outside-click cleanup in capture phase (prevents interfering with <summary>/<details> search)
 * - Ignore clicks/focus inside header/search UI
 * - Keep only ONE global listener set
 *
 * EXTRA FIX (discount code input on PDP / cart drawer):
 * - Do NOT interfere with cart drawer / cart notification / discount code UI
 * - Do NOT use global focusin cleanup (can break coupon input focus)
 *
 * EXTRA FIX (PDP main price discount UI):
 * - show original main price striked
 * - show discounted main price
 *
 * EXTRA FIX:
 * - add-ons must NOT change main product price
 *
 * EXTRA FIX (scroll jump):
 * - closed tooltips are fully non-focusable (hidden + aria-hidden + inert)
 * - guard against external accessibility scripts forcing focus into closed tips
 */

(function () {
  const SELECTORS = {
    container: '[data-js-addon-container]',
    item: '[data-js-addon-item]',
    checkbox: 'input[type="checkbox"][data-addon-variant-id]',
    infoBtn: '[data-js-addon-info-button]',
    modal: '[data-js-addon-modal]',
    modalClose: '[data-js-addon-modal-close]',
    overlay: '[data-js-addon-overlay]',
    tip: '[data-js-addon-tip]',
    tipClose: '[data-js-addon-tip-close]',
  };

  const STORAGE_KEY = 'lf_pending_addons_merge_callout_v1';
  const CART_CHANGED_EVENT = 'lf:cart-changed';

  // Written by discount-apply.js once it has resolved a discount code.
  const RESOLVED_DISCOUNT_KEY = 'scDiscountData';

  const CART_MUT_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;
  const CART_ADD_RE = /\/cart\/add(\.js)?(\?|$)/i;

  const HEADER_SEARCH_SELECTOR = [
    'sticky-header',
    '.header-wrapper',
    'header',
    'details-modal',
    '.header__search',
    '.search-modal',
    '.modal__toggle',
    '.modal-overlay',
    'predictive-search',
    '[data-predictive-search]',
    '#Search-In-Modal',
    'input[type="search"]',
    '.search__input',
    '.field__input',
    '.LIV-547-searchbar',
    '.LIV-547-searchbar-mobile',
  ].join(',');

  const PROTECTED_UI_SELECTOR = [
    HEADER_SEARCH_SELECTOR,
    'cart-drawer',
    '#CartDrawer',
    '.cart-drawer',
    '.drawer',
    '.drawer__inner',
    '.drawer__header',
    '.drawer__content',
    '.drawer__footer',
    'cart-notification',
    '.cart-notification',
    '.cart-notification-wrapper',
    '.cart-count-bubble',
    'cart-items',
    '.cart-items',
    '.totals',
    '.cart__footer',
    '.cart__blocks',
    '.cart__ctas',
    '.discounts',
    '[data-discount]',
    '[data-discount-form]',
    '[name="discount"]',
    '[name="discount_code"]',
    'input[name*="discount"]',
    'input[id*="discount"]',
    'input[autocomplete="off"]',
    'details',
    'summary',
    'input',
    'textarea',
    'select',
    'button',
    'label',
    '[contenteditable="true"]',
  ].join(',');

  function isInHeaderOrSearch(el) {
    try {
      return !!(el && el.closest && el.closest(HEADER_SEARCH_SELECTOR));
    } catch (e) {
      return false;
    }
  }

  function isInProtectedUi(el) {
    try {
      return !!(el && el.closest && el.closest(PROTECTED_UI_SELECTOR));
    } catch (e) {
      return false;
    }
  }

  function toInt(val, fallback = 0) {
    const n = parseInt(String(val ?? '').replace(/[^\d-]/g, ''), 10);
    return Number.isFinite(n) ? n : fallback;
  }

  function formatMoney(cents, moneyFormat) {
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, moneyFormat);
      }
    } catch (e) {}
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return amount + ' €';
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Failed to load cart');
    return res.json();
  }

  function getMoneyFormat(container) {
    return container.getAttribute('data-money-format') || '${{amount}}';
  }

  function getBasePrice(container) {
    return toInt(container.getAttribute('data-base-price'), 0);
  }

  function setBasePrice(container, cents) {
    container.setAttribute('data-base-price', String(toInt(cents, 0)));
  }

  function getPriceTarget(container) {
    const targetId = container.getAttribute('data-price-target-id');
    if (targetId) return document.getElementById(targetId);

    const sectionId = container.getAttribute('data-section-id');
    if (sectionId) return document.getElementById(`price-${sectionId}`);

    return null;
  }

  function findThemePriceNodes(priceRoot) {
    if (!priceRoot) {
      return {
        regular: null,
        saleCurrent: null,
        saleCompare: null,
        fallback: null,
      };
    }

    const regular =
      priceRoot.querySelector('.price__regular .price-item--regular') ||
      priceRoot.querySelector('.price__regular .price-item') ||
      priceRoot.querySelector('.price-item--regular');

    const saleCurrent =
      priceRoot.querySelector('.price__sale .price-item--sale.price-item--last') ||
      priceRoot.querySelector('.price__sale .price-item--sale') ||
      priceRoot.querySelector('.price-item--sale.price-item--last') ||
      priceRoot.querySelector('.price-item--sale');

    const saleCompare =
      priceRoot.querySelector('.price__sale .price-item--regular') ||
      null;

    const fallback =
      saleCurrent ||
      regular ||
      priceRoot.querySelector('.price-item') ||
      priceRoot.querySelector('.price') ||
      priceRoot;

    return { regular, saleCurrent, saleCompare, fallback };
  }

  function getStablePriceContainer(priceRoot) {
    if (!priceRoot) return null;
    return (
      priceRoot.querySelector(':scope > .price') ||
      priceRoot.querySelector('.price') ||
      priceRoot.querySelector('.price__container') ||
      priceRoot
    );
  }

  function getOrCreateMainPriceUi(priceRoot) {
    if (!priceRoot) return {};

    let host = priceRoot.querySelector(':scope > .AddOnProducts__MainPriceRow');
    if (!host) {
      const stableContainer = getStablePriceContainer(priceRoot);

      host = document.createElement('span');
      host.className = 'AddOnProducts__MainPriceRow';
      host.setAttribute('data-js-main-price-ui', 'true');
      host.hidden = true;

      if (stableContainer && stableContainer !== priceRoot && stableContainer.parentNode === priceRoot) {
        priceRoot.insertBefore(host, stableContainer);
      } else {
        priceRoot.prepend(host);
      }
    }

    let original = host.querySelector('.AddOnProducts__MainPriceOriginal');
    if (!original) {
      original = document.createElement('span');
      original.className = 'AddOnProducts__MainPriceOriginal';
      original.hidden = true;
      host.appendChild(original);
    }

    let discounted = host.querySelector('.AddOnProducts__MainPriceDiscounted');
    if (!discounted) {
      discounted = document.createElement('span');
      discounted.className = 'AddOnProducts__MainPriceDiscounted';
      discounted.hidden = true;
      host.appendChild(discounted);
    }

    return { host, original, discounted };
  }

  function updateSelectedUI(container) {
    container.querySelectorAll(SELECTORS.item).forEach((row) => {
      const cb = row.querySelector(SELECTORS.checkbox);
      row.classList.toggle('is-selected', !!(cb && cb.checked));
    });
  }

  function normalizeScopeId(raw) {
    const s = String(raw ?? '').trim();
    if (!s) return '';
    const parts = s.split('/');
    return (parts[parts.length - 1] || s).trim();
  }

  function readResolvedDiscount() {
    try {
      return JSON.parse(sessionStorage.getItem(RESOLVED_DISCOUNT_KEY)) || null;
    } catch (e) {
      return null;
    }
  }

  // The .price node rendered by snippets/price.liquid carries the product and collection
  // ids, which is what a discount's scope is expressed against.
  function getScopePriceElement(container) {
    const priceRoot = getPriceTarget(container);
    return priceRoot ? priceRoot.querySelector('.price') : null;
  }

  // discount-apply.js publishes the code it resolved against the Shopify Admin API along
  // with the collections/products it targets. Respect that scope: a code limited to one
  // collection must not discount an unrelated product.
  function resolvedDiscountAppliesTo(priceEl, resolved) {
    if (!priceEl || !resolved) return false;

    const productId = normalizeScopeId(priceEl.dataset.productId);
    if (productId && (resolved.products || []).includes(productId)) return true;

    const scoped = new Set(resolved.collections || []);
    return String(priceEl.dataset.collections || '')
      .split(',')
      .map(normalizeScopeId)
      .filter(Boolean)
      .some((id) => scoped.has(id));
  }

  // Used when the cart cannot report the discount yet: an empty cart never returns
  // discount_codes, so a customer arriving on /discount/<code> depends on this path.
  function getSessionDiscount(container) {
    const resolved = readResolvedDiscount();
    if (!resolvedDiscountAppliesTo(getScopePriceElement(container), resolved)) return null;

    const type = String(resolved.discount?.type || '');
    const value = parseFloat(resolved.discount?.value);
    if (!Number.isFinite(value) || value <= 0) return null;

    if (type === 'percentage') {
      return { active: true, factor: 1 - value / 100, pct: window.roundDiscount(value) };
    }

    if (type === 'fixed') {
      const basePrice = getBasePrice(container);
      if (basePrice <= 0) return null;
      const discounted = Math.max(0, basePrice - Math.round(value * 100));
      const factor = discounted / basePrice;
      return { active: true, factor, pct: window.roundDiscount((1 - factor) * 100) };
    }

    return null;
  }

  function findCartLine(cart, variantId) {
    const id = String(variantId || '');
    if (!id) return null;
    return (cart?.items || []).find((item) => String(item.variant_id) === id) || null;
  }

  // A line's own allocations are the only trustworthy statement that the cart's code
  // reaches this product. Cart-wide totals say nothing about an individual product: a code
  // that discounts some other item still drags total below original_total_price.
  function discountFromCartLine(line) {
    for (const alloc of line.line_level_discount_allocations || []) {
      const app = alloc.discount_application;
      if (app && app.value_type === 'percentage' && parseFloat(app.value) > 0) {
        const codePct = parseFloat(app.value);
        return { active: true, factor: 1 - codePct / 100, pct: window.roundDiscount(codePct) };
      }
    }

    // Fixed-amount and other discount types: derive the factor from this line alone, so it
    // is not diluted by the rest of the cart (deposit, add-ons, other products).
    const original = toInt(line.original_line_price, 0);
    const final = toInt(line.final_line_price, -1);
    if (original > 0 && final >= 0 && final < original) {
      const factor = final / original;
      return { active: true, factor, pct: window.roundDiscount((1 - factor) * 100) };
    }

    return null;
  }

  const NO_DISCOUNT = { active: false, factor: 1, pct: 0 };

  function getCartDiscount(container, cart) {
    const parentVariantId = container.getAttribute('data-parent-variant-id');
    const line = findCartLine(cart, parentVariantId);

    // The cart is authoritative for a product it actually contains: if this line carries
    // no discount, the code does not reach this product, however large the cart discount.
    if (line && String(line.properties?._addon || '') !== 'true') {
      return discountFromCartLine(line) || NO_DISCOUNT;
    }

    // Not in the cart, so the cart says nothing about it. Fall back to the discount
    // discount-apply.js resolved from the URL/cookie, which carries its own scope.
    return getSessionDiscount(container) || NO_DISCOUNT;
  }

  function updateMainPrice(container, cart) {
    const base = getBasePrice(container);

    const priceRoot = getPriceTarget(container);
    if (!priceRoot) return;

    const moneyFormat = getMoneyFormat(container);
    const ui = getOrCreateMainPriceUi(priceRoot);
    const theme = findThemePriceNodes(priceRoot);

    if (!cart) {
      if (theme.regular) theme.regular.textContent = formatMoney(base, moneyFormat);
      if (theme.saleCurrent) theme.saleCurrent.textContent = formatMoney(base, moneyFormat);
      if (theme.saleCompare) theme.saleCompare.textContent = formatMoney(base, moneyFormat);

      if (ui.original) {
        ui.original.hidden = true;
        ui.original.textContent = '';
      }
      if (ui.discounted) {
        ui.discounted.hidden = true;
        ui.discounted.textContent = '';
      }
      if (ui.host) ui.host.hidden = true;
      return;
    }

    const discount = getCartDiscount(container, cart);

    if (!discount.active) {
      if (theme.regular) theme.regular.textContent = formatMoney(base, moneyFormat);
      if (theme.saleCurrent) theme.saleCurrent.textContent = formatMoney(base, moneyFormat);
      if (theme.saleCompare) theme.saleCompare.textContent = formatMoney(base, moneyFormat);

      if (ui.original) {
        ui.original.hidden = true;
        ui.original.textContent = '';
      }
      if (ui.discounted) {
        ui.discounted.hidden = true;
        ui.discounted.textContent = '';
      }
      if (ui.host) ui.host.hidden = true;
      return;
    }

    const discountedBase = Math.round(base * discount.factor);

    if (theme.regular) theme.regular.textContent = '';
    if (theme.saleCurrent) theme.saleCurrent.textContent = '';
    if (theme.saleCompare) theme.saleCompare.textContent = '';

    if (ui.original) {
      ui.original.hidden = false;
      ui.original.textContent = formatMoney(base, moneyFormat);
    }

    if (ui.discounted) {
      ui.discounted.hidden = false;
      ui.discounted.textContent = formatMoney(discountedBase, moneyFormat);
    }

    if (ui.host) ui.host.hidden = false;
  }

  function applyDiscountToAddonRows(container, cart) {
    const parentDiscount = getCartDiscount(container, cart);
    const moneyFormat = getMoneyFormat(container);

    container.querySelectorAll(SELECTORS.item).forEach((row) => {
      const cb = row.querySelector(SELECTORS.checkbox);
      const priceWrap = row.querySelector('.AddOnItem__Price');
      const currentEl = row.querySelector('.AddOnItem__CurrentPrice');
      if (!cb || !priceWrap || !currentEl) return;

      let discountedEl = row.querySelector('.AddOnItem__DiscountedPrice');
      if (!discountedEl) {
        discountedEl = document.createElement('span');
        discountedEl.className = 'AddOnItem__DiscountedPrice';
        discountedEl.hidden = true;
        priceWrap.appendChild(discountedEl);
      }

      const addonCents = toInt(cb.getAttribute('data-addon-price'), 0);

      // An add-on already in the cart states its own discount. Otherwise preview with the
      // parent's, the best estimate available before the add-on is added.
      const addonLine = findCartLine(cart, cb.getAttribute('data-addon-variant-id'));
      const discount = addonLine ? discountFromCartLine(addonLine) || NO_DISCOUNT : parentDiscount;

      if (!discount.active || !addonCents) {
        row.classList.remove('is-cart-discount-active');
        discountedEl.hidden = true;
        discountedEl.textContent = '';
        return;
      }

      const discounted = Math.round(addonCents * discount.factor);
      row.classList.add('is-cart-discount-active');
      discountedEl.hidden = false;
      discountedEl.textContent = formatMoney(discounted, moneyFormat);
    });
  }

  function applyCartStateToUI(container, cart) {
    const inCart = new Set((cart?.items || []).map((it) => it.variant_id));

    container.querySelectorAll(SELECTORS.checkbox).forEach((cb) => {
      const vid = toInt(cb.getAttribute('data-addon-variant-id'), 0);
      cb.checked = inCart.has(vid);
    });

    updateSelectedUI(container);
    // updateMainPrice(container, cart);
    // applyDiscountToAddonRows(container, cart);
  }

  function createThrottled(fn, wait) {
    let t = null;
    return function () {
      if (t) return;
      t = setTimeout(() => {
        t = null;
        fn();
      }, wait);
    };
  }

  function bindCartResync(container) {
    const resync = createThrottled(async () => {
      try {
        const cart = await fetchCart();
        window.__lfAddonsLastKnownCartPdp = cart;
        applyCartStateToUI(container, cart);
      } catch (e) {}
    }, 200);

    document.addEventListener(CART_CHANGED_EVENT, resync);
    document.addEventListener('cart:updated', resync);
    document.addEventListener('cart:refresh', resync);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') resync();
    });
    window.addEventListener('focus', resync);
  }

  function openModal(container, id) {
    const overlay = container.querySelector(SELECTORS.overlay);
    const modal = container.querySelector(`[data-js-addon-modal="${CSS.escape(id)}"]`);
    if (overlay) overlay.classList.add('is-open');
    if (modal) modal.classList.add('is-open');
    document.documentElement.classList.add('AddonModalOpen');
  }

  function closeAllModals(container) {
    const overlay = container.querySelector(SELECTORS.overlay);
    if (overlay) overlay.classList.remove('is-open');
    container.querySelectorAll(SELECTORS.modal).forEach((m) => m.classList.remove('is-open'));

    if (document.documentElement.classList.contains('AddonModalOpen')) {
      document.documentElement.classList.remove('AddonModalOpen');
    }
  }

  function setTipClosedState(tip) {
    if (!tip) return;
    tip.classList.remove('is-open', 'is-above');
    tip.hidden = true;
    tip.setAttribute('aria-hidden', 'true');
    tip.setAttribute('inert', '');
    tip.style.top = '';
    tip.style.left = '';
    tip.style.removeProperty('--arrow-x');
  }

  function setTipOpenState(tip) {
    if (!tip) return;
    tip.hidden = false;
    tip.classList.add('is-open');
    tip.setAttribute('aria-hidden', 'false');
    tip.removeAttribute('inert');
  }

  function closeAllTips(container) {
    container.querySelectorAll(SELECTORS.tip).forEach((t) => {
      setTipClosedState(t);
    });
  }

  function closeAllTipsEverywhere() {
    document.querySelectorAll(SELECTORS.container).forEach((c) => closeAllTips(c));
  }

  function positionTip(buttonEl, tipEl) {
    if (!buttonEl || !tipEl) return;

    const btnRect = buttonEl.getBoundingClientRect();

    const wasClosed = tipEl.hidden || !tipEl.classList.contains('is-open');
    if (wasClosed) setTipOpenState(tipEl);

    const tipRect = tipEl.getBoundingClientRect();

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top = btnRect.bottom + 10;
    let left = btnRect.left + btnRect.width / 2 - tipRect.width / 2;

    left = Math.max(12, Math.min(left, vw - tipRect.width - 12));

    if (top + tipRect.height + 12 > vh) {
      top = btnRect.top - tipRect.height - 10;
      tipEl.classList.add('is-above');
    } else {
      tipEl.classList.remove('is-above');
    }

    top = Math.max(8, top);

    tipEl.style.top = `${top + window.scrollY}px`;
    tipEl.style.left = `${left + window.scrollX}px`;

    const arrowCenter = btnRect.left + btnRect.width / 2 - left;
    tipEl.style.setProperty('--arrow-x', `${arrowCenter}px`);
  }

  function bindTipHandlers(container) {
    container.addEventListener('click', (e) => {
      const infoBtn = e.target.closest(SELECTORS.infoBtn);
      if (infoBtn && container.contains(infoBtn)) {
        e.preventDefault();
        e.stopPropagation();

        const id = infoBtn.getAttribute('data-addon-info-target');
        const tip = container.querySelector(`[data-js-addon-tip="${CSS.escape(id)}"]`);

        if (!tip) {
          if (id) openModal(container, id);
          return;
        }

        const wasOpen = !tip.hidden && tip.classList.contains('is-open');
        closeAllTipsEverywhere();

        if (!wasOpen) {
          setTipOpenState(tip);
          requestAnimationFrame(() => positionTip(infoBtn, tip));
        }
        return;
      }

      const tipClose = e.target.closest(SELECTORS.tipClose);
      if (tipClose && container.contains(tipClose)) {
        e.preventDefault();
        closeAllTips(container);
        return;
      }

      const modalClose = e.target.closest(SELECTORS.modalClose);
      if (modalClose && container.contains(modalClose)) {
        e.preventDefault();
        closeAllModals(container);
        return;
      }

      const overlay = e.target.closest(SELECTORS.overlay);
      if (overlay && container.contains(overlay)) {
        e.preventDefault();
        closeAllModals(container);
      }
    });

    container.addEventListener('mouseleave', () => {
      closeAllTips(container);
    });
  }

  function ensureGlobalOutsideHandlers() {
    if (window.__lfAddonsCalloutGlobalOutsideBoundV4) return;
    window.__lfAddonsCalloutGlobalOutsideBoundV4 = true;

    document.addEventListener('click', (e) => {
      const target = e.target;

      if (isInHeaderOrSearch(target)) return;
      if (isInProtectedUi(target)) return;

      const insideAddon = target && target.closest ? target.closest(SELECTORS.container) : null;
      if (insideAddon) return;

      closeAllTipsEverywhere();
    });

    document.addEventListener(
      'focusin',
      (e) => {
        const tip = e.target && e.target.closest ? e.target.closest(SELECTORS.tip) : null;
        if (!tip) return;

        if (tip.hidden || tip.getAttribute('aria-hidden') === 'true' || tip.hasAttribute('inert')) {
          try {
            if (document.activeElement && typeof document.activeElement.blur === 'function') {
              document.activeElement.blur();
            }
          } catch (err) {}
        }
      },
      true
    );

    window.addEventListener('resize', () => closeAllTipsEverywhere());
    window.addEventListener('scroll', () => closeAllTipsEverywhere(), { passive: true });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeAllTipsEverywhere();
    });
  }

  function writePending(payload) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {}
  }

  function readPending() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function clearPending() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  function getSelectedAddonVariantIds(container) {
    const ids = [];
    container.querySelectorAll(SELECTORS.checkbox).forEach((cb) => {
      if (!cb.checked) return;
      const vid = toInt(cb.getAttribute('data-addon-variant-id'), 0);
      if (vid) ids.push(vid);
    });
    return ids;
  }

  function bindCheckboxUI(container) {
    container.addEventListener('change', (e) => {
      const cb = e.target && e.target.matches && e.target.matches(SELECTORS.checkbox) ? e.target : null;
      if (!cb) return;

      updateSelectedUI(container);

      // if (window.__lfAddonsLastKnownCartPdp) {
      //   updateMainPrice(container, window.__lfAddonsLastKnownCartPdp);
      //   applyDiscountToAddonRows(container, window.__lfAddonsLastKnownCartPdp);
      // } else {
      //   updateMainPrice(container, null);
      // }
    });
  }

  function bindFormCapture(container) {
    const formId = container.getAttribute('data-product-form-id');
    if (!formId) return;

    const form = document.getElementById(formId);
    if (!form) return;
    if (form.__lfAddonsCaptureBoundCallout) return;
    form.__lfAddonsCaptureBoundCallout = true;

    form.addEventListener(
      'submit',
      () => {
        const addonIds = getSelectedAddonVariantIds(container);
        if (!addonIds.length) {
          clearPending();
          return;
        }

        writePending({
          createdAt: Date.now(),
          addonVariantIds: addonIds,
          parentHandle: container.getAttribute('data-parent-handle') || '',
          parentVariantId: container.getAttribute('data-parent-variant-id') || '',
        });
      },
      true
    );
  }

  function bindVariantListeners(container) {
    document.addEventListener('variant:change', (e) => {
      const v = e.detail && e.detail.variant;
      if (!v || typeof v.price === 'undefined') return;
      setBasePrice(container, v.price);

      if (window.__lfAddonsLastKnownCartPdp) {
        updateMainPrice(container, window.__lfAddonsLastKnownCartPdp);
        applyDiscountToAddonRows(container, window.__lfAddonsLastKnownCartPdp);
      } else {
        updateMainPrice(container, null);
      }
    });
  }

  function buildItemsFromFormData(fd) {
    const mainId = toInt(fd.get('id'), 0);
    const qty = toInt(fd.get('quantity'), 1) || 1;

    const properties = {};
    for (const [k, v] of fd.entries()) {
      const m = String(k).match(/^properties\[(.+)\]$/);
      if (m) properties[m[1]] = v;
    }

    const item = { id: mainId, quantity: qty };
    if (Object.keys(properties).length) item.properties = properties;

    return mainId ? [item] : [];
  }

  function buildItemsFromUrlEncoded(bodyStr) {
    const usp = new URLSearchParams(bodyStr);
    const fd = new FormData();
    for (const [k, v] of usp.entries()) fd.append(k, v);
    return buildItemsFromFormData(fd);
  }

  function tryParseJSONBody(body) {
    try {
      return JSON.parse(body);
    } catch (e) {
      return null;
    }
  }

  function dispatchCartChanged() {
    try {
      document.dispatchEvent(new CustomEvent(CART_CHANGED_EVENT));
    } catch (e) {}
  }

  function ensureNetworkPatches() {
    if (!window.__lfAddonsNetworkPatchedCalloutV1) {
      window.__lfAddonsNetworkPatchedCalloutV1 = true;

      const originalFetch = window.fetch.bind(window);

      window.fetch = async function (input, init) {
        const url = typeof input === 'string' ? input : input && input.url ? input.url : '';
        const isCartMutation = CART_MUT_RE.test(url);
        const isCartAdd = CART_ADD_RE.test(url);

        if (
          isCartAdd &&
          !window.__lfAddonsMergingNowCallout &&
          init &&
          (init.method || 'POST').toUpperCase() === 'POST'
        ) {
          const pending = readPending();
          const isFresh = pending && Date.now() - (pending.createdAt || 0) <= 60 * 1000;
          const addonIds = isFresh && Array.isArray(pending.addonVariantIds) ? pending.addonVariantIds : [];

          if (addonIds.length) {
            try {
              window.__lfAddonsMergingNowCallout = true;

              let mainItems = [];
              const headers = new Headers(init.headers || {});
              const body = init.body;

              if (body instanceof FormData) {
                mainItems = buildItemsFromFormData(body);
              } else if (typeof body === 'string') {
                const json = tryParseJSONBody(body);
                if (json && Array.isArray(json.items)) {
                  mainItems = json.items;
                } else {
                  mainItems = buildItemsFromUrlEncoded(body);
                }
              } else if (body && typeof body === 'object') {
                if (Array.isArray(body.items)) mainItems = body.items;
              }

              const items = Array.isArray(mainItems) ? [...mainItems] : [];

              const parentHandle = pending.parentHandle || '';
              const parentVariantId = pending.parentVariantId || '';

              addonIds.forEach((vid) => {
                items.push({
                  id: vid,
                  quantity: 1,
                  properties: {
                    _addon: 'true',
                    _addon_for: parentHandle,
                    _addon_for_variant: parentVariantId,
                  },
                });
              });

              clearPending();

              headers.set('Content-Type', 'application/json');

              const newInit = {
                ...init,
                headers,
                body: JSON.stringify({ items }),
              };

              const res = await originalFetch(input, newInit);
              if (res && res.ok) dispatchCartChanged();
              return res;
            } catch (e) {
              clearPending();
              window.__lfAddonsMergingNowCallout = false;
              return originalFetch(input, init);
            } finally {
              window.__lfAddonsMergingNowCallout = false;
            }
          }
        }

        const res = await originalFetch(input, init);
        if (isCartMutation && res && res.ok) dispatchCartChanged();
        return res;
      };

      const OriginalXHR = window.XMLHttpRequest;
      function PatchedXHR() {
        const xhr = new OriginalXHR();
        let _url = '';

        const originalOpen = xhr.open;
        xhr.open = function (method, url) {
          _url = String(url || '');
          return originalOpen.apply(xhr, arguments);
        };

        xhr.addEventListener('load', function () {
          try {
            if (xhr.status >= 200 && xhr.status < 300 && CART_MUT_RE.test(_url)) {
              dispatchCartChanged();
            }
          } catch (e) {}
        });

        return xhr;
      }
      window.XMLHttpRequest = PatchedXHR;
    }
  }

  async function initContainer(container) {
    ensureNetworkPatches();
    ensureGlobalOutsideHandlers();

    bindTipHandlers(container);
    bindCheckboxUI(container);
    bindFormCapture(container);
    bindVariantListeners(container);
    bindCartResync(container);

    updateSelectedUI(container);
    closeAllTips(container);

    try {
      const cart = await fetchCart();
      window.__lfAddonsLastKnownCartPdp = cart;
      applyCartStateToUI(container, cart);
    } catch (e) {
      // updateMainPrice(container, null);
    }
  }

  function boot() {
    const containers = document.querySelectorAll(SELECTORS.container);
    if (!containers.length) return;
    containers.forEach((c) => initContainer(c));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();